import { Button } from 'omakase';
import { CurrencyContext } from '@tock/coretypes';
import {
  DisplayMoney,
  createMoneyFilter,
  currencyCodeToSymbol,
  isDecimalCurrency,
} from '@tock/money';
import { Form, Formik } from 'formik';
import { FormFormattedTextField, Grid } from 'ui';
import { InputAdornment } from '@material-ui/core';
import { useContext } from 'react';

interface Fields {
  price: string;
}

interface OnSubmitValues {
  price: number;
}

interface Props {
  onSubmit(values: OnSubmitValues): void;
  amountPaidCents?: number;
  currency?: string;
  purchasePriceCents?: number;
}

const filterWithDecimals = createMoneyFilter(2);
const filterNoDecimals = createMoneyFilter(0);

const TransferPriceForm: React.FunctionComponent<Props> = ({
  onSubmit,
  currency: currencyFromProps,
  purchasePriceCents,
  amountPaidCents,
}) => {
  const contextCurrency = useContext(CurrencyContext);
  const currency = currencyFromProps || contextCurrency;
  const hideDecimals = !isDecimalCurrency(currency);
  const inputFilter = !hideDecimals ? filterWithDecimals : filterNoDecimals;

  const formikValidate = (values: Fields) => {
    const paidCents = amountPaidCents || 0;
    const priceCents = Number(values.price);
    const moneyText = (
      <CurrencyContext.Provider value={currency} key={1}>
        <DisplayMoney amount={paidCents} />
      </CurrencyContext.Provider>
    );

    if (!values.price) {
      return {
        price: 'Required',
      };
    }

    if (priceCents < 50) {
      // Stripe has a requirement that charges be > $0.50
      const moneyText = (
        <CurrencyContext.Provider value={currency} key={1}>
          <DisplayMoney amount={50} />
        </CurrencyContext.Provider>
      );
      return {
        price: <div>Price must be greater than or equal to {moneyText}</div>,
      };
    } else if (priceCents > paidCents) {
      return { price: <div>Price must be less than or equal to {moneyText}</div> };
    } else {
      return {};
    }
  };

  return (
    <Formik<Fields>
      enableReinitialize
      initialValues={{
        price: inputFilter.formatModelValue(purchasePriceCents || 0),
      }}
      validate={formikValidate}
      onSubmit={(values: Fields) => onSubmit({ price: Number(values.price) })}
    >
      {({ isSubmitting, values }) => {
        const priceCents = Number(values.price);
        const disableButton =
          isSubmitting || !priceCents || priceCents < 50 || priceCents > (amountPaidCents || 0);
        return (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormFormattedTextField
                  fullWidth
                  name="price"
                  filter={inputFilter}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {currencyCodeToSymbol(currency)}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth
                  className="FormAction"
                  size="large"
                  disabled={disableButton}
                  type="submit"
                  variant="primary"
                >
                  Continue
                </Button>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default TransferPriceForm;
